import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus, FiEdit2, FiChevronDown, FiChevronRight,
  FiToggleLeft, FiToggleRight, FiArchive, FiSearch, FiX, FiImage,
} from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";

/* ─── helpers ──────────────────────────────────────────────────────── */
const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const STATUS_COLOR = { active: "#22c55e", inactive: "#f59e0b", archived: "#94a3b8" };

const EMPTY_CAT = {
  name: "", description: "", image: "", displayOrder: 0,
  status: "active", seoTitle: "", seoDescription: "",
};
const EMPTY_SUB = {
  name: "", description: "", image: "", displayOrder: 0,
  status: "active", seoTitle: "", seoDescription: "", categoryId: "",
};

/* ─── ImageUploadField ─────────────────────────────────────────────── */
const ImageUploadField = ({ value, onChange, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await API.post("/upload", fd);
      if (data.url) onChange(data.url);
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>{label}</label>
      {value && (
        <div style={{ marginBottom: 8, position: "relative", display: "inline-block" }}>
          <img src={value} alt="" style={{ height: 80, borderRadius: 6, border: "1px solid #e2e8f0", objectFit: "cover" }} />
          <button onClick={() => onChange("")} style={{ position: "absolute", top: -6, right: -6, background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}><FiX size={10} /></button>
        </div>
      )}
      <div>
        <label style={{ ...btn("secondary"), cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <FiImage size={13} /> {uploading ? "Uploading…" : value ? "Change Image" : "Upload Image"}
          <input type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
        </label>
      </div>
    </div>
  );
};

/* ─── Modal ────────────────────────────────────────────────────────── */
const Modal = ({ title, onClose, onSave, saving, children }) => (
  <div style={overlay}>
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={iconBtn}><FiX /></button>
      </div>
      {children}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <button onClick={onClose} style={btn("secondary")}>Cancel</button>
        <button onClick={onSave} disabled={saving} style={btn("primary")}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  </div>
);

/* ─── CategoryForm ─────────────────────────────────────────────────── */
const CategoryForm = ({ form, setForm }) => (
  <>
    <div style={row}>
      <div style={col}>
        <label style={lbl}>Category Name *</label>
        <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Visiting Cards" />
      </div>
      <div style={col}>
        <label style={lbl}>Slug (auto-generated)</label>
        <input style={{ ...inp, background: "#f8fafc", color: "#94a3b8" }} value={slugify(form.name || "")} readOnly />
      </div>
    </div>
    <ImageUploadField label="Category Image" value={form.image} onChange={v => setForm(f => ({ ...f, image: v }))} />
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>Description</label>
      <textarea style={{ ...inp, minHeight: 70, resize: "vertical" }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
    </div>
    <div style={row}>
      <div style={col}>
        <label style={lbl}>Display Order</label>
        <input type="number" style={inp} value={form.displayOrder} onChange={e => setForm(f => ({ ...f, displayOrder: parseInt(e.target.value) || 0 }))} />
      </div>
      <div style={col}>
        <label style={lbl}>Status</label>
        <select style={inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>
    </div>
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>SEO Title</label>
      <input style={inp} value={form.seoTitle} onChange={e => setForm(f => ({ ...f, seoTitle: e.target.value }))} />
    </div>
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>SEO Description</label>
      <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.seoDescription} onChange={e => setForm(f => ({ ...f, seoDescription: e.target.value }))} />
    </div>
  </>
);

/* ─── SubcategoryForm ──────────────────────────────────────────────── */
const SubcategoryForm = ({ form, setForm, categories }) => (
  <>
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>Parent Category *</label>
      <select style={inp} value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}>
        <option value="">— Select Category —</option>
        {categories.filter(c => c.status !== "archived").map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
    <div style={row}>
      <div style={col}>
        <label style={lbl}>Subcategory Name *</label>
        <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Premium" />
      </div>
      <div style={col}>
        <label style={lbl}>Slug (auto-generated)</label>
        <input style={{ ...inp, background: "#f8fafc", color: "#94a3b8" }} value={slugify(form.name || "")} readOnly />
      </div>
    </div>
    <ImageUploadField label="Subcategory Image" value={form.image} onChange={v => setForm(f => ({ ...f, image: v }))} />
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>Description</label>
      <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
    </div>
    <div style={row}>
      <div style={col}>
        <label style={lbl}>Display Order</label>
        <input type="number" style={inp} value={form.displayOrder} onChange={e => setForm(f => ({ ...f, displayOrder: parseInt(e.target.value) || 0 }))} />
      </div>
      <div style={col}>
        <label style={lbl}>Status</label>
        <select style={inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>
    </div>
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>SEO Title</label>
      <input style={inp} value={form.seoTitle} onChange={e => setForm(f => ({ ...f, seoTitle: e.target.value }))} />
    </div>
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>SEO Description</label>
      <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.seoDescription} onChange={e => setForm(f => ({ ...f, seoDescription: e.target.value }))} />
    </div>
  </>
);

/* ─── Main Component ───────────────────────────────────────────────── */
export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [expanded, setExpanded]     = useState({});

  // Category modal
  const [catModal, setCatModal]   = useState(false);
  const [catEditing, setCatEditing] = useState(null);
  const [catForm, setCatForm]     = useState(EMPTY_CAT);
  const [catSaving, setCatSaving] = useState(false);

  // Subcategory modal
  const [subModal, setSubModal]     = useState(false);
  const [subEditing, setSubEditing] = useState(null);
  const [subForm, setSubForm]       = useState(EMPTY_SUB);
  const [subSaving, setSubSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/catalog/categories");
      setCategories(data.categories || []);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = categories.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  /* Category actions */
  const openAddCat  = () => { setCatEditing(null); setCatForm(EMPTY_CAT); setCatModal(true); };
  const openEditCat = (cat) => { setCatEditing(cat); setCatForm({ name: cat.name, description: cat.description || "", image: cat.image || "", displayOrder: cat.displayOrder, status: cat.status, seoTitle: cat.seoTitle || "", seoDescription: cat.seoDescription || "" }); setCatModal(true); };

  const saveCat = async () => {
    if (!catForm.name.trim()) { toast.error("Name is required"); return; }
    setCatSaving(true);
    try {
      if (catEditing) {
        await API.put(`/catalog/categories/${catEditing.id}`, catForm);
        toast.success("Category updated");
      } else {
        await API.post("/catalog/categories", catForm);
        toast.success("Category created");
      }
      setCatModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
    finally { setCatSaving(false); }
  };

  const toggleCatStatus = async (cat) => {
    const newStatus = cat.status === "active" ? "inactive" : "active";
    try {
      await API.put(`/catalog/categories/${cat.id}`, { status: newStatus });
      toast.success(`Category ${newStatus}`);
      load();
    } catch { toast.error("Failed to update status"); }
  };

  const archiveCat = async (cat) => {
    if (!window.confirm(`Archive "${cat.name}"? This hides it from product assignment.`)) return;
    try {
      await API.put(`/catalog/categories/${cat.id}`, { status: "archived" });
      toast.success("Category archived");
      load();
    } catch { toast.error("Failed"); }
  };

  /* Subcategory actions */
  const openAddSub  = (catId) => { setSubEditing(null); setSubForm({ ...EMPTY_SUB, categoryId: catId }); setSubModal(true); };
  const openEditSub = (sub)   => { setSubEditing(sub); setSubForm({ name: sub.name, description: sub.description || "", image: sub.image || "", displayOrder: sub.displayOrder, status: sub.status, seoTitle: sub.seoTitle || "", seoDescription: sub.seoDescription || "", categoryId: sub.categoryId }); setSubModal(true); };

  const saveSub = async () => {
    if (!subForm.name.trim() || !subForm.categoryId) { toast.error("Category and name are required"); return; }
    setSubSaving(true);
    try {
      if (subEditing) {
        await API.put(`/catalog/subcategories/${subEditing.id}`, subForm);
        toast.success("Subcategory updated");
      } else {
        await API.post("/catalog/subcategories", subForm);
        toast.success("Subcategory created");
      }
      setSubModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Error"); }
    finally { setSubSaving(false); }
  };

  if (loading) return <div style={{ padding: 40, color: "#64748b" }}>Loading categories…</div>;

  return (
    <div style={{ padding: "24px 28px", width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Categories</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", marginTop: 2 }}>{categories.length} categories</p>
        </div>
        <button onClick={openAddCat} style={btn("primary")}><FiPlus size={14} style={{ marginRight: 6 }} />Add Category</button>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 20, maxWidth: 340 }}>
        <FiSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} size={14} />
        <input style={{ ...inp, paddingLeft: 32 }} placeholder="Search categories…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Category list */}
      {filtered.map(cat => (
        <div key={cat.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
          {/* Category row */}
          <div style={{ display: "flex", alignItems: "center", padding: "14px 16px", gap: 12 }}>
            <button onClick={() => setExpanded(p => ({ ...p, [cat.id]: !p[cat.id] }))} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex" }}>
              {expanded[cat.id] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </button>

            {cat.image
              ? <img src={cat.image} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover", border: "1px solid #e2e8f0" }} />
              : <div style={{ width: 40, height: 40, borderRadius: 6, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}><FiImage size={16} color="#94a3b8" /></div>
            }

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{cat.name}</div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>/{cat.slug} · {cat.productCount ?? 0} products · {cat.subcategories?.length ?? 0} subcategories</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLOR[cat.status], background: STATUS_COLOR[cat.status] + "18", padding: "2px 8px", borderRadius: 20 }}>{cat.status}</span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>#{cat.displayOrder}</span>

              <button title="Edit" onClick={() => openEditCat(cat)} style={iconBtn}><FiEdit2 size={13} /></button>
              <button title={cat.status === "active" ? "Deactivate" : "Activate"} onClick={() => toggleCatStatus(cat)} style={iconBtn}>
                {cat.status === "active" ? <FiToggleRight size={15} color="#22c55e" /> : <FiToggleLeft size={15} color="#94a3b8" />}
              </button>
              {cat.status !== "archived" && (
                <button title="Archive" onClick={() => archiveCat(cat)} style={iconBtn}><FiArchive size={13} color="#f59e0b" /></button>
              )}
              <button onClick={() => { setExpanded(p => ({ ...p, [cat.id]: true })); openAddSub(cat.id); }} style={{ ...btn("secondary"), fontSize: 11, padding: "4px 10px" }}>
                <FiPlus size={11} style={{ marginRight: 4 }} />Subcategory
              </button>
            </div>
          </div>

          {/* Subcategories */}
          {expanded[cat.id] && (
            <div style={{ borderTop: "1px solid #f1f5f9", background: "#f8fafc" }}>
              {(!cat.subcategories || cat.subcategories.length === 0) && (
                <div style={{ padding: "12px 24px", fontSize: 13, color: "#94a3b8" }}>No subcategories yet.</div>
              )}
              {(cat.subcategories || []).map(sub => (
                <div key={sub.id} style={{ display: "flex", alignItems: "center", padding: "10px 24px", borderBottom: "1px solid #f1f5f9", gap: 10 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_COLOR[sub.status], flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{sub.name}</span>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{sub.status}</span>
                  <button title="Edit" onClick={() => openEditSub({ ...sub, categoryId: cat.id })} style={iconBtn}><FiEdit2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {filtered.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>No categories found.</div>
      )}

      {/* Category Modal */}
      {catModal && (
        <Modal title={catEditing ? "Edit Category" : "Add Category"} onClose={() => setCatModal(false)} onSave={saveCat} saving={catSaving}>
          <CategoryForm form={catForm} setForm={setCatForm} />
        </Modal>
      )}

      {/* Subcategory Modal */}
      {subModal && (
        <Modal title={subEditing ? "Edit Subcategory" : "Add Subcategory"} onClose={() => setSubModal(false)} onSave={saveSub} saving={subSaving}>
          <SubcategoryForm form={subForm} setForm={setSubForm} categories={categories} />
        </Modal>
      )}
    </div>
  );
}

/* ─── Styles ───────────────────────────────────────────────────────── */
const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#fff" };
const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 5 };
const row = { display: "flex", gap: 14, marginBottom: 12 };
const col = { flex: 1 };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const card   = { background: "#fff", borderRadius: 12, padding: 28, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" };
const iconBtn = { background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 5, display: "flex", alignItems: "center", borderRadius: 5 };
const btn = (v) => ({
  display: "inline-flex", alignItems: "center", border: "none", borderRadius: 7,
  padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 500,
  ...(v === "primary"
    ? { background: "var(--primary,#6366f1)", color: "#fff" }
    : { background: "#f1f5f9", color: "#374151" }),
});
